import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import {
  Badge, EmptyState, PageLoading, SearchInput,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import DistributorDetailDialog from './DistributorDetailDialog';
import type { Distributor } from '../../types';

const STATUS_TONE = {
  active: 'success',
  invited: 'warning',
  suspended: 'danger',
  deactivated: 'neutral',
} as const;

const STATUS_LABEL = {
  active: 'Active',
  invited: 'Not joined yet',
  suspended: 'Suspended',
  deactivated: 'Removed',
} as const;

/**
 * Distributors, from the company admin's side.
 *
 * Shows every distributor tied to this company: their name, contact,
 * assigned areas, retailer count and status. Clicking a row opens the
 * detail dialog where areas and retailers are managed.
 *
 * Distributors are created from the Team page (they are users with
 * role='distributor'), so there is no "Add" button here — the page
 * is about managing the distributor's territory, not their login.
 */
export default function DistributorsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [distributors, setDistributors] = useState<Distributor[] | null>(null);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ distributors: Distributor[]; nextCursor: string | null }>('/distributors');
      setDistributors(res.distributors);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load distributors.');
      setDistributors([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (distributors ?? []).filter((d) =>
      !needle
      || d.fullName.toLowerCase().includes(needle)
      || d.email?.toLowerCase().includes(needle)
      || d.mobile?.includes(needle),
    );
  }, [distributors, search]);

  const activeCount = (distributors ?? []).filter((d) => d.status === 'active').length;

  return (
    <>
      <PageHeader
        title="Distributors"
        subtitle={distributors
          ? `${activeCount} active distributor${activeCount === 1 ? '' : 's'}`
          : undefined}
        actions={isAdmin ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              // Navigate to Team page — distributors are added as users
              window.location.href = '/team';
            }}
          >
            <Icon name="plus" size={16} /> Add from Team
          </button>
        ) : undefined}
      />

      <div className="page">
        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search distributors" />
        </div>

        <div className="card">
          {!distributors ? (
            <div className="card-pad"><PageLoading rows={3} /></div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon="users"
              title={search ? 'No distributors match that' : 'No distributors yet'}
              body={search
                ? 'Try a different name, email or area.'
                : 'Distributors are intermediaries between your company and your retailers. Add one from the Team page with the Distributor role, then assign areas here.'}
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Areas</th>
                    <th className="num">Retailers</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((d) => (
                    <tr
                      key={d.id}
                      className="clickable"
                      onClick={() => setOpenId(d.id)}
                    >
                      <td className="primary">{d.fullName}</td>
                      <td>
                        <div className="small">{d.email ?? '—'}</div>
                        <div className="small faint">{d.mobile ?? ''}</div>
                      </td>
                      <td>
                        {d.areaCount === 0
                          ? <span className="faint">None</span>
                          : `${d.areaCount} area${d.areaCount === 1 ? '' : 's'}`}
                      </td>
                      <td className="num">{d.retailerCount}</td>
                      <td>
                        <Badge tone={STATUS_TONE[d.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
                          {STATUS_LABEL[d.status as keyof typeof STATUS_LABEL] ?? d.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {openId ? (
        <DistributorDetailDialog
          distributorUserId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </>
  );
}
